import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { OperativeInfo } from 'src/app/domain-model/operative-info.class';
import { IJobTitleInfo } from "../../../message-models/job-title.interface";
import { IJobLevelInfo } from "../../../message-models/job-level.interface";
import { IRiskInfo } from "../../../message-models/risk-info.interface";
import { OperativesService } from "../../services/operatives.service";
import { consts } from "../../../common/modals/consts";
import { UntypedFormControl, Validators } from '@angular/forms';
import { IDateTimeWithZone } from '../../../common/date-time-with-zone.interface';
import { IEmploymentStatus } from '../../../message-models/employment-status.interface';
import { ElectricalWorkType } from '../../../message-models/electrical-work-type';
import { IOperativeTypeInfo } from '../../../message-models/operative-type.interface';
import { IBusinessUnitInfo } from '../../../message-models/business-unit.interface';
import { CompanyJobRoleService } from '../../../setting/job-role/services/company-job-role.service';
import { ICompanyJobTitle } from '../../../message-models/company-job-title.interface';
import { OurUserService } from '../../../users/services/user.service';
import { IUserInfo } from '../../../users/models/userInfo.interface';

@Component({
  selector: 'app-operative-editor',
  templateUrl: './operative-editor.component.html',
  styleUrls: ['./operative-editor.component.scss']
})
export class OperativeEditorComponent implements OnInit {
  @Input() operative: OperativeInfo;
  @Input() isNewOperative: boolean = false;

  @Input() allOperativeTypes: IOperativeTypeInfo[] = [];
  @Input() allBusinessUnits: IBusinessUnitInfo[] = [];
  @Input() allJobTitles: IJobTitleInfo[] = [];
  @Input() allCompanyJobTitles: ICompanyJobTitle[] = [];
  @Input() allJobLevels: IJobLevelInfo[] = [];
  @Input() allRisks: IRiskInfo[] = [];
  @Input() allEmploymentStatuses: IEmploymentStatus[] = [];

  //Filtered lists based on current state
  filteredJobTitles: IJobTitleInfo[] = [];
  filteredJobLevels: IJobLevelInfo[] = [];
  filteredRisks: IRiskInfo[] = [];

  //Selected items
  selectedJobTitle: IJobTitleInfo;
  selectedJobLevel: IJobLevelInfo;
  selectedRisk: IRiskInfo;

  @Input() saveAttempted: boolean = false;
  @Output() saveOperativeRequested: EventEmitter<OperativeInfo> = new EventEmitter<OperativeInfo>();
  @Output() fileRemove: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() fileUpload: EventEmitter<File> = new EventEmitter<File>();
  @Output() formStatusChange: EventEmitter<any> = new EventEmitter<File>();

  isQsRole: boolean;
  isNonElectricalJobRole: boolean;
  emailAddress: UntypedFormControl;
  editingOperativeDetails: boolean = false;
  isOnMobile: boolean = false;
  editingCurrentEngagement: boolean = false;
  matchingOperatives: OperativeInfo[];
  selectedLastName: string = "";
  operativeMaxDate: Date;
  operativeMinDate: Date;
  filetypeTooltip: string;
  maxFileSize: number = 1000000;
  acceptedFileTypes = '.png, .jpg, .jpeg,';
  operativeImageFile: File[] = [];
  showJobTitleUpdate: boolean;
  showJobLevelUpdate: boolean;
  businessUnitHelpText: string;

  selectedCompanyJobTitle: ICompanyJobTitle;
  defaultJobJevelId: string;
  loggedInUser: IUserInfo;
  selectedJobLevelId: string;

  columns: any[] = [
    { field: 'fullName', header: 'Name', type: 'text' },
    { field: 'referenceNumbers', header: 'Reference Nos', type: 'text' },
    { field: 'startedOn', header: 'Started On', type: 'date' },
    { field: 'endedOn', header: 'Ended On', type: 'date' }
  ];
  showMatchingOperatives = true;
  private tempSelectedLastName = '';

  constructor(private operativesService: OperativesService, private companyJobRoleService: CompanyJobRoleService, private userService: OurUserService) {
  }

  ngOnInit(): void {
    this.isOnMobile = window.outerWidth <= 480;
    this.editingOperativeDetails = this.isNewOperative;
    this.editingCurrentEngagement = this.isNewOperative;
    if (this.operative && this.operative?.latestEngagementFrom?.dateTime) {
      this.disableDatesPriorSelected(this.operative?.latestEngagementFrom);
    }
    if (this.isNewOperative) {
      this.operative.setDefaultEmploymentStatus(this.allEmploymentStatuses);
    }

    this.userService.getLoggedInUser().subscribe(user => {
      this.loggedInUser = user;
    });
  
    this.selectedJobTitle = this.allJobTitles.find(d => d.id == this.operative.currentJobTitleId);
    this.selectedJobLevel = this.allJobLevels.find(d => d.id == this.operative.currentJobLevelId);
    this.selectedRisk = this.allRisks.find(d => d.id == this.operative.currentRiskId);
    this.businessUnitHelpText = this.allBusinessUnits?.length < 1 ? "You have no Business Units defined. This field is optional but if you wish to create some business units then please go to the Business Units tab on the settings page." : "Choose the Business Unit that best suits the Operatives current role.";
  }

  saveOperative() {
    this.saveOperativeRequested.emit(this.operative);
  }

  cancelEditing() {
  }

  lastNameChanged(lastNameStartsWith: string) {
    if (lastNameStartsWith === '' || !lastNameStartsWith.includes(this.tempSelectedLastName)) {
      this.showMatchingOperatives = true;
    }
    if (lastNameStartsWith && lastNameStartsWith.length > 2) {
      this.operativesService.findOperativesWithLastNameStartingWith(this.operative.companyId, lastNameStartsWith).subscribe(operatives => {
        this.matchingOperatives = operatives;
      });
    }
    else {
      this.matchingOperatives = [];
    }
  }

  isNewOperativeSelected() {
    if (!this.isNewOperative)
      return false;

    return !this.operative?.id || this.operative.id === consts.emptyGuid;
  }

  operativeSelected(operative: OperativeInfo) {
    if (operative) {
      this.matchingOperatives = [];

      this.operative.id = operative.id;
      this.operative.firstName = operative.firstName;
      this.operative.lastName = operative.lastName;
      this.operative.emailAddress = operative.emailAddress;
      this.operative.referenceNumbers = operative.referenceNumbers;
      this.operative.phoneNumber = operative.phoneNumber;
      this.operative.latestEngagementFrom = operative.latestEngagementFrom;
      this.operative.latestEngagementTo = operative.latestEngagementTo;
      this.operative.currentRiskId = operative.currentRiskId;
      this.operative.currentJobLevelId = operative.currentJobLevelId;
      this.operative.currentJobTitleId = operative.currentJobTitleId;
      this.operative.currentOperativeTypeId = operative.currentOperativeTypeId;
      this.operative.currentBusinessUnitId = operative.currentBusinessUnitId;
    }
  }

  disableDatesPriorSelected(date: IDateTimeWithZone) {
    let selectedDate = new Date(date.dateTime);
    this.operativeMinDate = new Date(selectedDate.setDate(selectedDate.getDate() + 1));
  }

  disableDatesPostSelected(date: IDateTimeWithZone) {
    let selectedDate = new Date(date.dateTime);
    this.operativeMaxDate = new Date(selectedDate.setDate(selectedDate.getDate() - 1));
  }

  getSelectedJobTitle(): IJobTitleInfo {
    return this.filteredJobTitles.find(t => t.id === this.operative.currentJobTitleId);
  }

  updateJobTitles(operativeTypeId: string, showUpdateAlert: boolean) {
    //this.showJobTitleUpdate = showUpdateAlert;
    this.operative.currentOperativeTypeId = operativeTypeId;

    if (operativeTypeId == "4eff2451-6793-4590-82c1-fc189dc47859") {
      this.filteredJobTitles = this.allCompanyJobTitles;
      this.selectedJobTitle = this.filteredJobTitles.find(x => x.id === this.operative.currentJobTitleId);      
    }
    else {
      this.filteredJobTitles = this.allJobTitles.filter(f => f.operativeTypeId == this.operative.currentOperativeTypeId);
      this.selectedJobTitle = this.filteredJobTitles.find(x => x.id === this.operative.currentJobTitleId);
    }
 
    //job title not found so set to first value in filtered list
    if (!this.selectedJobTitle) {
      this.selectedJobTitle = this.filteredJobTitles[0];
    }
    this.operative.currentJobTitleId = this.selectedJobTitle?.id; 
    this.updateJobLevel(this.operative.currentJobTitleId, showUpdateAlert);
  }

  updateJobLevel(jobTitleId: string, showUpdateAlert: boolean) { 
    this.operative.currentJobTitleId = jobTitleId; 
    if (this.operative.currentOperativeTypeId == "4eff2451-6793-4590-82c1-fc189dc47859") {
      this.selectedCompanyJobTitle = this.allCompanyJobTitles.find(x => x.id === this.operative.currentJobTitleId);
      this.isQsRole = false;
      this.isNonElectricalJobRole = true;
      //this.defaultJobJevelId = this.selectedCompanyJobTitle.defaultJobLevelId;
      this.defaultJobJevelId = this.allCompanyJobTitles.find(x => x.id === this.operative.currentJobTitleId)?.defaultJobLevelId;

    }
    else {
      this.isQsRole = this.filteredJobTitles.find(d => d.id === jobTitleId)?.electricalWorkType == ElectricalWorkType.QualifiedSupervisor;
      this.isNonElectricalJobRole = this.filteredJobTitles.find(d => d.id === jobTitleId)?.electricalWorkType == ElectricalWorkType.NonElectrical;
      this.defaultJobJevelId = this.filteredJobTitles.find(d => d.id === jobTitleId)?.defaultJobLevelId;
    }
    
    if (this.isNonElectricalJobRole) {
      this.filteredJobLevels = this.allJobLevels.filter(f => f.index === 0);
    }
    else if (this.isQsRole) {
      this.filteredJobLevels = this.allJobLevels.filter(f => f.id == this.defaultJobJevelId);
    }
    else {
      this.filteredJobLevels = this.allJobLevels.filter(f => f.operativeTypeId == this.operative.currentOperativeTypeId).sort((a, b) => a.index - b.index);
    }
    //this.selectedJobLevel = this.filteredJobLevels.find(x => x.id === this.defaultJobJevelId);
    this.selectedJobLevelId = this.filteredJobLevels.find(x => x.id === this.defaultJobJevelId)?.id;
    //job level not found so set to default value for the selected job title
    if (!this.selectedJobLevelId) {
      //var defaultJobJevel = this.selectedJobTitle.defaultJobLevelId;
      //this.selectedJobLevel = this.filteredJobLevels.find(x => x.id === this.defaultJobJevelId)?.id;
      this.selectedJobLevelId = this.filteredJobLevels.find(x => x.id === this.defaultJobJevelId)?.id;

    }
    //this.operative.currentJobLevelId = this.selectedJobLevel?.id;
    this.operative.currentJobLevelId = this.selectedJobLevelId;
    this.updateRisk(this.operative.currentJobLevelId);
  }

  updateRisk(jobLevelId: string) {
    this.operative.currentJobLevelId = jobLevelId;
    if (this.isNonElectricalJobRole) {
      this.filteredRisks = this.allRisks.filter(f => f.index === 0);
    }
    else {
      this.filteredRisks = this.allRisks.filter(f => f.operativeTypeId == this.operative.currentOperativeTypeId).sort((a, b) => a.index - b.index);
    }
    this.selectedRisk = this.filteredRisks.find(x => x.id === this.operative.currentRiskId);
    //risk not found so set to first value in filtered list
    if (!this.selectedRisk) {
      this.selectedRisk = this.filteredRisks[0];
    }
    this.operative.currentRiskId = this.selectedRisk?.id;
  }

  validateEmail(emailAddress: string) {
    this.emailAddress = new UntypedFormControl(emailAddress,
      [Validators.email, Validators.pattern("^(?=.{1,64}@)[A-Za-z0-9_-]+(\\.[A-Za-z0-9_-]+)*@[^-][A-Za-z0-9-]+(\\.[A-Za-z0-9-]+)*(\\.[A-Za-z]{2,})$")]);
    if (this.editingOperativeDetails) {
      return this.emailAddress.valid;
    } else {
      return true;
    }
  }

  hideMatchingOperative() {
    this.showMatchingOperatives = false;
    this.tempSelectedLastName = this.selectedLastName;
  }

  addOperativeImage(imageString: string) {
    this.operative.image = imageString;
  }
}
